import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useCopy } from '../lib/copy';
import { highlight } from '../lib/highlight';
import { installText, PMS, setPm, usePm } from '../lib/pm';

export const CopyIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="8" y="8" width="12" height="12" rx="2.5" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" /></svg>;
export const CheckIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>;
export const CloseIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>;
export const BlobMark = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 26 26" aria-hidden="true"><path d="M13 2.5c6.4 0 10.5 3.9 10.5 10.2 0 6.6-4.4 10.8-10.6 10.8C6.6 23.5 2.5 19.4 2.5 13 2.5 6.5 6.7 2.5 13 2.5z" fill="currentColor" /><rect x="8.6" y="9.4" width="2.4" height="5.6" rx="1.2" fill="var(--bg)" /><rect x="14.8" y="9" width="2.4" height="5.6" rx="1.2" fill="var(--bg)" /></svg>
);

/** A row of toggle buttons; the pressed one is the value. */
export function Seg<T extends string | number>({ value, options, onChange, className = '', label, labelledBy, id }: {
  value: T; options: [T, ReactNode, string?][]; onChange: (v: T) => void; className?: string; label?: string; labelledBy?: string; id?: string;
}) {
  return (
    <div className={'seg ' + className} role="group" aria-label={label} aria-labelledby={labelledBy} id={id}>
      {options.map(([v, text, title]) => (
        <button key={String(v)} type="button" aria-pressed={v === value} title={title} onClick={() => onChange(v)}>{text}</button>
      ))}
    </div>
  );
}

/** The install command with a package manager switch, used in the hero and the share dialog. */
export function Install() {
  const pm = usePm();
  const text = installText(pm);
  const [done, setDone] = useState(false);
  const cmd = useRef<HTMLSpanElement>(null);
  const copy = () => {
    const ok = () => { setDone(true); setTimeout(() => setDone(false), 1600); };
    const select = () => { const r = document.createRange(); if (cmd.current) r.selectNodeContents(cmd.current); const s = getSelection(); s?.removeAllRanges(); s?.addRange(r); };
    try { navigator.clipboard.writeText(text).then(ok, select); } catch { select(); }
  };
  return (
    <div className="inst" role="group" aria-label="Install">
      <div className="seg sm" role="tablist" aria-label="Package manager">
        {PMS.map(p => <button key={p} type="button" role="tab" aria-selected={p === pm} onClick={() => setPm(p)}>{p}</button>)}
      </div>
      <div className="inst-cmd">
        <code><span className="tk-p">$</span><span className="inst-text" ref={cmd}>{text}</span></code>
        <button type="button" className={'copy-btn' + (done ? ' done' : '')} aria-label="Copy install command" title="Copy" onClick={copy}>{done ? <CheckIcon /> : <CopyIcon />}</button>
      </div>
    </div>
  );
}

/** Code with tabs and a copy button. */
export function CodeBox<K extends string>({ tabs, value, onTab, code, tall, label, id }: {
  tabs: [K, string][]; value: K; onTab: (k: K) => void; code: string; tall?: boolean; label: string; id: string;
}) {
  const pre = useRef<HTMLElement>(null);
  const [msg, copy] = useCopy('Copy code');
  return (
    <div className="code-box">
      <div className="code-head">
        <div className="seg sm" role="tablist" aria-label={label}>
          {tabs.map(([k, t]) => <button key={k} type="button" role="tab" id={id + '-' + k} aria-selected={k === value} aria-controls={id} onClick={() => onTab(k)}>{t}</button>)}
        </div>
        <button type="button" className="link-btn" onClick={() => copy(code, pre.current)}>{msg}</button>
      </div>
      <pre className={'code-pre' + (tall ? ' tall' : '')} id={id} role="tabpanel" aria-labelledby={id + '-' + value} tabIndex={0}><code ref={pre}>{highlight(code)}</code></pre>
    </div>
  );
}

/** A modal <dialog>: open while `open` is true; closes on Escape or a click on the backdrop. */
export function Dialog({ open, onClose, className = 'share', labelledBy, children }: { open: boolean; onClose: () => void; className?: string; labelledBy: string; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) { if (d.showModal) d.showModal(); else d.setAttribute('open', ''); }
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className={className} aria-labelledby={labelledBy} onClose={onClose} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      {open ? children : null}
    </dialog>
  );
}

// ---------- toast ----------
const ToastCtx = createContext<(msg: string) => void>(() => {});
export const useToast = () => useContext(ToastCtx);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState('');
  const [on, setOn] = useState(false);
  const t = useRef<ReturnType<typeof setTimeout> | null>(null);
  const show = useCallback((m: string) => {
    setMsg(m);
    requestAnimationFrame(() => setOn(true));
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => { setOn(false); setTimeout(() => setMsg(''), 250); }, 3600);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div className={'toast' + (on ? ' is-on' : '')} role="status" aria-live="polite">
        {msg ? <><i aria-hidden="true"><CheckIcon /></i><span>{msg}</span></> : null}
      </div>
    </ToastCtx.Provider>
  );
}
